import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: '一人で行っても大丈夫ですか?',
      answer: '大丈夫です。初回参加者の7割は一人で来ています。現地で自然に会話が始まるので、「ぼっち」にはなりません。心配な方は、事前に相談所で「初参加で不安です」と伝えてください。当日、案内役がつきます。'
    },
    {
      question: '何も決まっていない状態で相談してもいいですか?',
      answer: 'むしろ、そういう方が多いです。「吉野、気になる。でも何から?」という状態で相談にくる方がほとんど。未定は前向きな状態です。"未定です"って4回言っても大丈夫。一緒に入口を探しましょう。'
    },
    {
      question: '週末だけの参加でも意味がありますか?',
      answer: '意味あります。むしろ週末参加の方が多いです。草刈り1日、古民家掃除1日、それだけでも現場は進みます。「続けられるか分からない」と思っても、まず1回来てみてください。続けるかどうかは、来てから決めればいいので。'
    },
    {
      question: '特別なスキルがなくても参加できますか?',
      answer: '参加できます。草刈り、掃除、片付け、話を聞く、写真を撮る、SNSで発信する...誰でもできることが、実は一番必要です。「何もできない」と思っている人ほど、現場で「あれ、これならできる」を見つけていきます。'
    },
    {
      question: '移住を考えていないと参加できませんか?',
      answer: '移住前提ではありません。「週末だけ吉野人」「月1回だけ参加」「オンラインで応援」など、関わり方は自由です。移住するかどうかは、関わっていく中で自然に決まっていくものなので。'
    },
    {
      question: '年齢制限はありますか?',
      answer: '制限はありません。20代から60代まで、幅広い年代の方が参加しています。「自分の年齢で浮かないか心配」という声もありますが、現地では年齢よりも「何に興味があるか」「どんな経験をしてきたか」が話題になります。'
    },
    {
      question: '費用はかかりますか?',
      answer: '活動参加は基本無料です。交通費・宿泊費・食費は自己負担となります。「お試し参加」の場合は、昼食を用意していることもあります。費用が心配な方は、相談時に「予算が限られています」と伝えてください。できる範囲で調整します。'
    },
    {
      question: '宿泊先はどうすればいいですか?',
      answer: '近隣のゲストハウスや民宿を紹介できます。また、メンバーの中には「泊まれるよ」という方もいます。初回の方には、宿泊先リストをお送りしますので、相談時に「宿泊先を知りたい」とお伝えください。'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 bg-stone-50">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-6">
            よくある『不安』に答えます
          </h2>
          <p className="text-lg md:text-xl text-gray-700">
            「気になるけど、でも...」その"でも"を、ひとつずつ解消していきましょう
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 flex items-start justify-between gap-4 text-left cursor-pointer hover:bg-stone-50 transition-colors"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <div className="flex items-start gap-4 flex-1">
                  <span className="bg-amber-100 text-amber-700 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 mt-1">
                    Q
                  </span>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 leading-relaxed">
                    {faq.question}
                  </h3>
                </div>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  <i 
                    className="ri-arrow-down-s-line text-2xl text-gray-600 w-6 h-6 flex items-center justify-center" 
                    aria-hidden="true"
                  ></i>
                </motion.div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    id={`faq-answer-${index}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-6 pt-2">
                      <div className="flex items-start gap-4 bg-stone-50 rounded-lg p-6">
                        <span className="bg-stone-700 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 mt-1">
                          A
                        </span>
                        <p className="text-base md:text-lg text-gray-700 leading-relaxed flex-1">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-12"
        >
          <p className="text-base md:text-lg text-gray-600">
            他にも不安なことがあれば、<br className="md:hidden" />
            <a href="/consultation" className="text-amber-700 font-bold hover:text-amber-800 underline underline-offset-4">
              吉野なんでも相談所
            </a>
            で何でも聞いてください
          </p>
        </motion.div>
      </div>

      {/* Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}
      </script>
    </section>
  );
};

export default FAQSection;
